import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'

import {
  ApiError,
  UnauthorizedError,
  referenceLibraryApi,
  type CatalogDocumentDTO,
} from '@/shared/api'
import { useAuth } from '@/features/auth'

import type {
  ReferenceSelectionContextValue,
  ReferenceSelectionState,
} from './types'

const INITIAL_STATE: ReferenceSelectionState = {
  documents: [],
  status: 'idle',
  error: null,
  selectedIds: [],
}

const Ctx = createContext<ReferenceSelectionContextValue | null>(null)

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

interface Props {
  children: ReactNode
}

/**
 * Holds the per-session selection of reference documents that should
 * complement the next chat message. Selection lives **in memory only** —
 * we never persist tenant ids or document ids to LocalStorage, and we never
 * store tokens (cookie-only auth model).
 *
 * The provider also owns a snapshot of the tenant's reference library so the
 * composer popover and the chat sources panel can render labels without
 * round-tripping to ``/catalog/documents`` for every render. The snapshot is
 * refreshed automatically on tenant change and on explicit ``reload()``.
 */
export function ReferenceSelectionProvider({ children }: Props) {
  const { organization, status: authStatus, logout } = useAuth()
  const [state, setState] = useState<ReferenceSelectionState>(INITIAL_STATE)
  const requestedTenantRef = useRef<string | null>(null)

  const tenantId = organization?.tenant_id ?? null

  const reload = useCallback(
    async (): Promise<CatalogDocumentDTO[]> => {
      if (!tenantId) return []
      setState((prev) => ({ ...prev, status: 'loading', error: null }))
      try {
        const response = await referenceLibraryApi.list({ tenantId })
        setState((prev) => {
          const documents = response.items
          // Drop selections that no longer exist in the catalog so the next
          // chat request never sends a stale reference_document_id (the
          // backend would 404 it; surfacing the change locally is cleaner).
          const known = new Set(documents.map((d) => d.document_id))
          const selectedIds = prev.selectedIds.filter((id) => known.has(id))
          return {
            documents,
            status: 'ready',
            error: null,
            selectedIds,
          }
        })
        return response.items
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await logout()
          return []
        }
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: errorMessage(err, 'Falha ao carregar referências.'),
        }))
        return []
      }
    },
    [tenantId, logout],
  )

  // Boot / refresh on tenant change.
  useEffect(() => {
    if (authStatus !== 'authenticated' || !tenantId) {
      requestedTenantRef.current = null
      setState(INITIAL_STATE)
      return
    }
    if (requestedTenantRef.current === tenantId) return
    requestedTenantRef.current = tenantId
    void reload()
  }, [authStatus, tenantId, reload])

  const toggle = useCallback((documentId: string) => {
    const clean = documentId.trim()
    if (!clean) return
    setState((prev) => {
      if (prev.selectedIds.includes(clean)) {
        return {
          ...prev,
          selectedIds: prev.selectedIds.filter((id) => id !== clean),
        }
      }
      return { ...prev, selectedIds: [...prev.selectedIds, clean] }
    })
  }, [])

  const add = useCallback((documentId: string) => {
    const clean = documentId.trim()
    if (!clean) return
    setState((prev) => {
      if (prev.selectedIds.includes(clean)) return prev
      return { ...prev, selectedIds: [...prev.selectedIds, clean] }
    })
  }, [])

  const remove = useCallback((documentId: string) => {
    const clean = documentId.trim()
    if (!clean) return
    setState((prev) => {
      if (!prev.selectedIds.includes(clean)) return prev
      return {
        ...prev,
        selectedIds: prev.selectedIds.filter((id) => id !== clean),
      }
    })
  }, [])

  const clear = useCallback(() => {
    setState((prev) =>
      prev.selectedIds.length === 0 ? prev : { ...prev, selectedIds: [] },
    )
  }, [])

  const value = useMemo<ReferenceSelectionContextValue>(() => {
    const docById = new Map(
      state.documents.map((d) => [d.document_id, d] as const),
    )
    const selectedDocuments = state.selectedIds
      .map((id) => docById.get(id))
      .filter((doc): doc is CatalogDocumentDTO => Boolean(doc))
    return {
      ...state,
      reload,
      toggle,
      add,
      remove,
      clear,
      selectedDocuments,
      isDocumentAvailable: (doc) =>
        doc.chunk_count > 0 &&
        (doc.status === null || doc.status === 'indexed'),
    }
  }, [state, reload, toggle, add, remove, clear])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useReferenceSelection(): ReferenceSelectionContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error(
      'useReferenceSelection must be used inside <ReferenceSelectionProvider>',
    )
  }
  return ctx
}
